import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
// src/features/editor/components/blocks/QuizAnswerRow.tsx
import { Trash2 } from 'lucide-react'
import type { QuizAnswer } from '@/types/blocks'
import { Button, cn } from '@teach/ui'
import { AutoGrowTextField } from './shared/AutoGrowTextField'
import { charsOk, wordsOk } from '@/features/editor/lib/quizWordLimits'
import { CharCount, WordCount } from './shared/QuizFieldCounters'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  index: number
  answer: QuizAnswer
  /** Лимит слов; без него поле ограничено только символами. */
  wordMax?: number
  /** Backend's hard character cap — a guard so a pathological input never 422s. */
  charMax: number
  /** false hides the delete button — used to enforce the 2-option minimum. */
  canRemove: boolean
  onChangeText: (index: number, text: string) => void
  onToggleCorrect: (index: number) => void
  onRemove: (index: number) => void
}

export const QuizAnswerRow = ({
  index,
  answer,
  wordMax,
  charMax,
  canRemove,
  onChangeText,
  onToggleCorrect,
  onRemove,
}: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const text = answer.text ?? ''

  const handleChange = (value: string) => {
    if (!charsOk(value, charMax, text)) return
    if (wordMax !== undefined && !wordsOk(value, wordMax, text)) return
    onChangeText(index, value)
  }

  // Квадратик и корзина — по центру ПОЛЯ, а не строки со счётчиком: счётчик
  // уходит отдельной строкой под поле.
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onToggleCorrect(index)}
          aria-label={answer.isCorrect ? m.correctAnswer : m.markCorrect}
          aria-pressed={answer.isCorrect}
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 transition-all',
            answer.isCorrect
              ? 'border-success bg-success text-success-foreground'
              : 'border-border bg-card hover:border-success',
          )}
        >
          {answer.isCorrect ? <span className="text-xs font-bold">✓</span> : null}
        </button>
        <AutoGrowTextField
          value={text}
          onChange={handleChange}
          maxLength={charMax}
          placeholder={cm.variantN(index + 1)}
          className="flex-1"
        />
        {/* Корзина всегда на месте (поле не растягивается); удалить нельзя —
            правильный вариант или минимум вариантов — она просто неактивна. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          aria-label={m.a11yRemove}
          className="shrink-0 rounded-xl text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="pl-9 pr-12">
        {wordMax !== undefined ? (
          <WordCount value={text} max={wordMax} />
        ) : (
          <CharCount value={text} max={charMax} />
        )}
      </div>
    </div>
  )
}
